"use client";

import type { ReactNode } from "react";
import { useState } from "react";

import { DiscoverFilterFields } from "@/components/app/discover-sort-constants";
import { Button } from "@/components/ui/button";
import type { BasicSort } from "@/contexts/filters-sort-context";
import { SortType } from "@/contexts/filters-sort-context";
import {
  APP_NAME_GENERIC,
  DESO_APP_NAME,
  FOCUS_LAUNCH_DATE,
} from "@/utils/constants";

import { DiscoverProvider } from "../../discover/discover-context";
import { LaunchedTokenTab } from "./launched-token-tab/launched-token-tab";
import { RecentTokenTab } from "./recent-token-tab/recent-token-tab";
import { UpcomingTokensTab } from "./upcoming-tokens-tab/upcoming-tokens-tab";

export enum LAUNCHPADS_SUB_TAB {
  UPCOMING = "upcoming",
  LAUNCHED = "launched",
  RECENT = "recent",
}

const LAUNCHPAD_TITLE_MAP = {
  [LAUNCHPADS_SUB_TAB.UPCOMING]: "Upcoming Launches",
  [LAUNCHPADS_SUB_TAB.LAUNCHED]: "Recently Launched",
  [LAUNCHPADS_SUB_TAB.RECENT]: "All Tokens",
};

const LAUNCHPAD_DESCRIPTION_MAP = {
  [LAUNCHPADS_SUB_TAB.UPCOMING]: `Get early access to ${DESO_APP_NAME} tokens launching on ${APP_NAME_GENERIC}`,
  [LAUNCHPADS_SUB_TAB.LAUNCHED]: `Recently launched ${DESO_APP_NAME} tokens on ${APP_NAME_GENERIC}`,
  [LAUNCHPADS_SUB_TAB.RECENT]: `All ${DESO_APP_NAME} tokens on ${APP_NAME_GENERIC}`,
};

const TAB_LABEL_MAP = {
  [LAUNCHPADS_SUB_TAB.UPCOMING]: "Upcoming",
  [LAUNCHPADS_SUB_TAB.LAUNCHED]: "Launched",
  [LAUNCHPADS_SUB_TAB.RECENT]: "Recent",
};

export const DISABLED_FILTERS: DiscoverFilterFields[] = [
  DiscoverFilterFields.lockedReferralRewardsUsdCents,
  DiscoverFilterFields.twitterUsername,
  DiscoverFilterFields.twitterTotalFollowers,
  DiscoverFilterFields.twitterTotalFollowing,
  DiscoverFilterFields.twitterTotalTweets,
  DiscoverFilterFields.twitterIsVerified,
  DiscoverFilterFields.unlockedJoiningRewardUsdCents,
  DiscoverFilterFields.totalReferralRewardsUsdCents,
  DiscoverFilterFields.lockedJoiningRewardUsdCents,
  DiscoverFilterFields.unlockedReferralRewardsUsdCents,
  DiscoverFilterFields.desoBalanceNanos,
  DiscoverFilterFields.usdcBalanceUsdCents,
  DiscoverFilterFields.usdtBalanceUsdCents,
  DiscoverFilterFields.totalPosts,
  DiscoverFilterFields.totalSubscriptionEarningsUsdCents,
  DiscoverFilterFields.totalDiamondsUsdCentsSent,
  DiscoverFilterFields.totalDiamondsUsdCentsReceived,
  DiscoverFilterFields.totalLiquidAssetsBalanceUsdCents,
  DiscoverFilterFields.totalReactionsReceived,
  DiscoverFilterFields.totalPositiveReactionsReceived,
  DiscoverFilterFields.totalNegativeReactionsReceived,
  DiscoverFilterFields.totalReactionsGiven,
  DiscoverFilterFields.totalPositiveReactionsGiven,
  DiscoverFilterFields.totalNegativeReactionsGiven,
  DiscoverFilterFields.totalReactions,
  DiscoverFilterFields.totalPaidMessageEarningsUsdCents,
];

export const LaunchpadDiscoverProvider = ({
  extraFilters,
  initialSort,
  children,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraFilters?: Record<string, any>;
  initialSort: BasicSort<DiscoverFilterFields>;
  children: ReactNode;
}) => {
  return (
    <DiscoverProvider
      queryToUse="tokenMetadata"
      extraFilters={extraFilters}
      fetchFreshItems
      initialFilters={[]}
      disabledFilters={DISABLED_FILTERS}
      initialSort={initialSort}
      useStorage={false}
    >
      {children}
    </DiscoverProvider>
  );
};

export const LaunchpadLayout = () => {
  const [subtab, setSubtab] = useState<LAUNCHPADS_SUB_TAB>(
    LAUNCHPADS_SUB_TAB.UPCOMING,
  );

  return (
    <>
      <LaunchpadHeader
        {...{
          subtab,
          setSubTab: setSubtab,
        }}
      />
      {subtab === LAUNCHPADS_SUB_TAB.UPCOMING && (
        <LaunchpadDiscoverProvider
          extraFilters={{
            tokenAmmLaunchAt: {
              isNull: false,
              greaterThan: new Date().toISOString(),
            },
            tokenAmmCreatedAt: {
              greaterThan: FOCUS_LAUNCH_DATE,
            },
          }}
          initialSort={
            {
              type: SortType.basic,
              name: DiscoverFilterFields.tokenAmmLaunchAt,
              direction: "asc",
            } as BasicSort<DiscoverFilterFields>
          }
        >
          <UpcomingTokensTab />
        </LaunchpadDiscoverProvider>
      )}
      {subtab === LAUNCHPADS_SUB_TAB.LAUNCHED && (
        <LaunchpadDiscoverProvider
          extraFilters={{
            tokenAmmLaunchAt: {
              isNull: false,
              lessThan: new Date().toISOString(),
            },
            tokenAmmCreatedAt: {
              greaterThan: FOCUS_LAUNCH_DATE,
            },
          }}
          initialSort={
            {
              type: SortType.basic,
              name: DiscoverFilterFields.tokenAmmLaunchAt,
              direction: "desc",
            } as BasicSort<DiscoverFilterFields>
          }
        >
          <LaunchedTokenTab />
        </LaunchpadDiscoverProvider>
      )}
      {subtab === LAUNCHPADS_SUB_TAB.RECENT && (
        <LaunchpadDiscoverProvider
          extraFilters={{
            tokenAmmCreatedAt: {
              isNull: false,
              lessThan: new Date().toISOString(),
              greaterThan: FOCUS_LAUNCH_DATE,
            },
          }}
          initialSort={
            {
              type: SortType.basic,
              name: DiscoverFilterFields.tokenAmmCreatedAt,
              direction: "desc",
            } as BasicSort<DiscoverFilterFields>
          }
        >
          <RecentTokenTab />
        </LaunchpadDiscoverProvider>
      )}
    </>
  );
};

const LaunchpadHeader = ({
  subtab,
  setSubTab,
}: {
  subtab: LAUNCHPADS_SUB_TAB;
  setSubTab: (subtab: LAUNCHPADS_SUB_TAB) => void;
}) => {
  const title = LAUNCHPAD_TITLE_MAP[subtab];
  const description = LAUNCHPAD_DESCRIPTION_MAP[subtab];

  return (
    <div
      className="
        flex flex-col gap-4 px-6
        sm:flex-row sm:items-end sm:justify-between sm:gap-0
      "
    >
      <div>
        <h2 className="font-sans text-xl font-semibold text-muted-foreground">
          {title}
        </h2>
        <p className="text-muted">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex gap-2 rounded-full">
          {Object.values(LAUNCHPADS_SUB_TAB).map((tab) => (
            <Button
              key={tab}
              variant={subtab === tab ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setSubTab(tab);
              }}
            >
              {TAB_LABEL_MAP[tab]}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
